import { Button, Card, Descriptions, Message, Popconfirm, Space, Table, Tag } from "@arco-design/web-react";
import { WorkOrderAPI, WOPOAPI, WoFulfillmentAPI } from "@/apis/manufacturing";
import { useCallback, useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { WorkOrderStatusDef, WorkOrderStatus, WOPOStatusDef, WOPOStatus, WOFulfillmentStatusDef, WOFulfillmentStatus } from "@/enums/manufacturing";
import { EnumLabel } from "@/components/Enum";
import { IconEdit } from "@arco-design/web-react/icon";
import { formatDate } from "@/utils";
import { Number } from "@/components/Numeric";

export const WorkOrderInfo = () => {
    const params = useParams();
    const woId = params.woId;
    const navigate = useNavigate();
    const [info, setInfo] = useState({});
    const [poList, setPOList] = useState([]);
    const [poTotal, setPoTotal] = useState(0);
    const [poPage, setPoPage] = useState(1);

    const [fulfillments, setFulfillments] = useState([]);
    const [fulTotal, setFulTotal] = useState(0);
    const [fulPage, setFulPage] = useState(1);

    const getInfo = useCallback(async () => {
        try {
            const values = await WorkOrderAPI.get(woId);
            setInfo(values);
        } catch (error) {
            Message.error(error.message);
        }
    }, [woId]);

    const getPOs = useCallback(async () => {
        try {
            const values = await WOPOAPI.query(poPage, { workOrderId: woId });
            setPOList(values.records);
            setPoTotal(values.total);
        } catch (error) {
            Message.error(error.message);
        }
    }, [poPage, woId]);

    const getFulfillments = useCallback(async () => {
        try {
            const values = await WoFulfillmentAPI.query(fulPage, { workOrderId: woId });
            setFulfillments(values.records);
            setFulTotal(values.total);
        } catch (error) {
            Message.error(error.message);
        }
    }, [fulPage, woId]);

    useEffect(() => {
        getInfo();
    }, [getInfo]);

    useEffect(() => {
        getPOs();
    }, [getPOs]);

    useEffect(() => {
        getFulfillments();
    }, [getFulfillments]);

    const handleIssue = async () => {
        try {
            await WorkOrderAPI.issue(info.id);
            Message.success("Work Order issued successfully");
            getInfo();
        } catch (error) {
            Message.error(error.message);
        }
    };

    const handleUnissue = async () => {
        try {
            await WorkOrderAPI.unissue(info.id);
            Message.success("Work Order unissued successfully");
            getInfo();
        } catch (error) {
            Message.error(error.message);
        }
    };

    const handleShortClose = async () => {
        try {
            await WorkOrderAPI.shortClose(info.id);
            Message.success("Work Order short closed successfully");
            getInfo();
        } catch (error) {
            Message.error(error.message);
        }
    };

    const handleVoid = async () => {
        try {
            await WorkOrderAPI.void(info.id);
            Message.success("Work Order voided successfully");
            getInfo();
        } catch (error) {
            Message.error(error.message);
        }
    };

    const canFulfill = info.status === WorkOrderStatus.Picked ||
        info.status === WorkOrderStatus.PartialPicked ||
        info.status === WorkOrderStatus.PartialFulfilled ||
        info.status === WorkOrderStatus.PartialPickedFulfilled;

    const hasActiveDocs = poList.some((po) => po.status !== WOPOStatus.Voided) ||
        fulfillments.some((ful) => ful.status !== WOFulfillmentStatus.Voided);

    const finishedItemColumns = [
        { title: "Item Name", dataIndex: "itemName" },
        { title: "Description", dataIndex: "description" },
        { title: "WO Qty", dataIndex: "quantity", render: (val) => <Number value={val} /> },
        { title: "UOM", dataIndex: "uom" },
    ];

    const rawItemColumns = [
        { title: "Item Name", dataIndex: "itemName" },
        { title: "Description", dataIndex: "description" },
        { title: "WO Qty", dataIndex: "quantity", render: (val) => <Number value={val} /> },
        { title: "UOM", dataIndex: "uom" },
    ];

    const fulfillmentColumns = [
        {
            title: "FW#",
            dataIndex: "fulfillmentNo",
            render: (col, record) =>
                <Link to={`/workspace/manufacturing/wo-fulfillment/${record.id}`}>{record.fulfillmentNo}</Link>
        },
        { title: "Fulfillment Date", dataIndex: "fulfillDate", render: (val) => val ? formatDate(val) : "" },
        { title: "Qty", dataIndex: "fulfilledQty", render: (val) => <Number value={val} /> },
        { title: "Status", dataIndex: "status", render: (col, record) => <EnumLabel enumObj={WOFulfillmentStatusDef} value={record.status} /> },
    ];

    return (
        <>
            <Card title={
                <Space>
                    <span>{info.workOrderNo}</span>
                    <Tag><EnumLabel enumObj={WorkOrderStatusDef} value={info.status} /></Tag>
                </Space>
            } extra={
                <Space>
                    {info.status === WorkOrderStatus.Created && (
                        <>
                            <Button type="primary" icon={<IconEdit />} onClick={() => navigate(`/workspace/manufacturing/work-order/${woId}/edit`)}>Edit</Button>
                            <Button type="primary" onClick={handleIssue}>Issue</Button>
                            <Button type="primary" status="success" onClick={() => navigate(`/workspace/manufacturing/wo-pick-order/wo/${woId}`)}>Pick</Button>
                            <Popconfirm title="Are you sure you want to void this Work Order?" onOk={handleVoid}>
                                <Button type="primary" status="danger">Void</Button>
                            </Popconfirm>
                        </>
                    )}
                    {info.status === WorkOrderStatus.Issued && (
                        <>
                            {!hasActiveDocs && (
                                <Button type="primary" onClick={handleUnissue}>Unissue</Button>
                            )}
                            <Button type="primary" status="success" onClick={() => navigate(`/workspace/manufacturing/wo-pick-order/wo/${woId}`)}>Pick</Button>
                            <Popconfirm title="Are you sure you want to void this Work Order?" onOk={handleVoid}>
                                <Button type="primary" status="danger">Void</Button>
                            </Popconfirm>
                        </>
                    )}
                    {(info.status === WorkOrderStatus.Picked) && (
                        <>
                            <Button type="primary" status="success" onClick={() => navigate(`/workspace/manufacturing/wo-pick-order/wo/${woId}`)}>Pick</Button>
                            {canFulfill && (
                                <Button type="primary" status="success" onClick={() => navigate(`/workspace/manufacturing/wo-fulfillment/add/${info.id}`)}>Fulfill</Button>
                            )}
                        </>
                    )}
                    {(info.status === WorkOrderStatus.PartialPicked || info.status === WorkOrderStatus.PartialFulfilled || info.status === WorkOrderStatus.PartialPickedFulfilled) && (
                        <>
                            <Button type="primary" status="success" onClick={() => navigate(`/workspace/manufacturing/wo-pick-order/wo/${woId}`)}>Pick</Button>
                            {canFulfill && (
                                <Button type="primary" status="success" onClick={() => navigate(`/workspace/manufacturing/wo-fulfillment/add/${info.id}`)}>Fulfill</Button>
                            )}
                            <Popconfirm title="Are you sure you want to short close this Work Order?" onOk={handleShortClose}>
                                <Button type="primary" status="warning">Short Close</Button>
                            </Popconfirm>
                        </>
                    )}
                    <Button onClick={() => navigate("/workspace/manufacturing/work-order")}>Back</Button>
                </Space>
            }>
                <Descriptions colon=":" column={4} data={[
                    { label: "WO#", value: info.workOrderNo },
                    { label: "BOM Name", value: info.bomId ? <Link to={`/workspace/manufacturing/bom/${info.bomId}`}>{info.bomName}</Link> : info.bomName },
                    { label: "Quantity", value: info.quantity },
                    { label: "Waste Factor", value: info.wasteFactor != null ? `${info.wasteFactor}%` : "" },
                    { label: "Production Date", value: info.productionDate || "" },
                    { label: "Created Date", value: info.createTime ? formatDate(info.createTime) : "" },
                    { label: "Notes", value: info.notes, span: 4 },
                ]} />

                <div style={{ marginTop: 24, marginBottom: 16 }}>
                    <span style={{ fontWeight: 600, fontSize: 14 }}>Finished Items</span>
                </div>
                <Table rowKey="id" columns={finishedItemColumns} data={info.finishedItems || []} pagination={false} />

                <div style={{ marginTop: 24, marginBottom: 16 }}>
                    <span style={{ fontWeight: 600, fontSize: 14 }}>Raw Items</span>
                </div>
                <Table rowKey="id" columns={rawItemColumns} data={info.rawItems || []} pagination={false} />

                <div style={{ marginTop: 24, marginBottom: 16 }}>
                    <span style={{ fontWeight: 600, fontSize: 14 }}>Pick Orders</span>
                </div>
                <Table rowKey="id" style={{ marginTop: 0 }} columns={[
                    {
                        title: "PKW#",
                        dataIndex: "pickOrderNo",
                        render: (col, record) =>
                            <Link to={"/workspace/manufacturing/wo-pick-order/" + record.id}>{record.pickOrderNo}</Link>
                    },
                    { title: "Created Date", dataIndex: "createTime", render: (val) => val ? formatDate(val) : "" },
                    { title: "Pick Date", dataIndex: "pickDate", render: (val) => val ? formatDate(val) : "" },
                    { title: "Qty", dataIndex: "pickQty", render: (val) => <Number value={val} /> },
                    { title: "Status", dataIndex: "status", render: (col, record) => <EnumLabel enumObj={WOPOStatusDef} value={record.status} /> },
                ]} data={poList} pagination={{ total: poTotal, current: poPage, pageSize: 20 }} onChange={(pagination) => setPoPage(pagination.current)} />

                {fulfillments.length > 0 && (
                    <>
                        <div style={{ marginTop: 24, marginBottom: 16 }}>
                            <span style={{ fontWeight: 600, fontSize: 14 }}>Fulfillments</span>
                        </div>
                        <Table rowKey="id" columns={fulfillmentColumns} data={fulfillments} pagination={{ total: fulTotal, current: fulPage, pageSize: 20 }} onChange={(pagination) => setFulPage(pagination.current)} />
                    </>
                )}
            </Card>

        </>
    );
};
